// Exam Prep API Utility
// Each endpoint has a unique base URL (no shared BASE_URL)

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Helper to make API requests
 * @param {string} url - Full endpoint URL
 * @param {object} options
 * @param {string} [token] - Optional auth token
 */
async function apiRequest(url, options = {}, token) {
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const requestOptions = {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {}),
    },
  };

  try {
    const res = await fetch(url, requestOptions);
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (jsonErr) {
      data = text;
    }
    if (!res.ok) {
      console.error("[ExamPrep][API ERROR]", {
        url,
        status: res.status,
        statusText: res.statusText,
        response: data,
      });
      throw new Error((data && data.message) || res.statusText);
    }

    return data;
  } catch (err) {
    console.error("[ExamPrep][API FETCH ERROR]", {
      url,
      error: err,
    });
    throw err;
  }
}

// --- API Endpoints ---

export function purchaseExamPrepPlan(studentId, token) {
  return apiRequest(
    `https://purchaseexamprepplan-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify({ studentId }),
    },
    token,
  );
}

export function bookIntroductoryCall(data, token) {
  return apiRequest(
    `https://bookintroductorycall-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token,
  );
}

export function markIntroCallDone(data, token) {
  return apiRequest(
    `https://markintrocalldone-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token,
  );
}

export function bookExamPrepClass(data, token) {
  return apiRequest(
    `https://bookexamprepclass-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token,
  );
}

export function getExamPrepStatus(studentId, token) {
  return apiRequest(
    `https://getexamprepstatus-${API_BASE_URL}?studentId=${encodeURIComponent(
      studentId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function setIntroCallSlots(data, token) {
  return apiRequest(
    `https://setintroductorycallslots-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token,
  );
}

export function setExamPrepClassSlots(data, token) {
  return apiRequest(
    `https://setexamprepclassslots-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token,
  );
}

export function getIntroCallSlots(tutorId, token) {
  return apiRequest(
    `https://getintroductorycallslots-${API_BASE_URL}?tutorId=${encodeURIComponent(
      tutorId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function getExamPrepClassSlots(tutorId, token) {
  return apiRequest(
    `https://getexamprepclassslots-${API_BASE_URL}?tutorId=${encodeURIComponent(
      tutorId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function testGrantExamPrepPlan(studentId, tutorId, token) {
  return apiRequest(
    `https://testgrantexamprepplan-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify({ studentId, tutorId }),
    },
    token,
  );
}

/**
 * Query GetStream directly for all channels where the user is a member
 * This replaces the Firebase function and queries Stream directly from the frontend
 */
export async function getUserChannels(userId, token) {
  try {
    // Import Stream client and ensure connection
    const { streamClient } = await import("../config/stream");
    const { ensureStreamConnection } = await import("../services/streamConnectionService");
    const { db } = await import("../firebaseConfig");
    const { doc, getDoc } = await import("firebase/firestore");

    // Ensure Stream client is connected to the correct user
    // Only connect if not already connected to the correct user
    if (!streamClient.isConnected || streamClient.userID !== userId) {
    await ensureStreamConnection(async () => {
      // Operation succeeds if we get here - ensureStreamConnection handles connection
      return true;
    }, userId);
    }

    // Query all channels where user is a member
    const filter = {
      members: { $in: [userId] },
      type: {
        $in: [
          "standard_group",
          "premium_group",
          "premium_individual_class",
          "one_to_one_chat",
          "exam_prep", 
        ],
      },
    };

    // Query channels with full state
    const channels = await streamClient.queryChannels(
      filter,
      { last_message_at: -1 },
      {
        watch: true,
        state: true,
        presence: true,
        limit: 100,
      }
    );

    // Enrich channels with Firestore data if missing
    // Filter out nulls (channels where user is not a member or don't exist in Firestore)
    const enrichedChannels = (await Promise.all(
      channels.map(async (channel) => {
        // Initialize data object - handle cases where channel.data might be undefined, null, or missing
        // Also check if Stream stores data in different places
        // IMPORTANT: Don't copy language from Stream - we'll get it from Firestore (source of truth)
        let channelDataObj = {};
        
        if (channel.data && typeof channel.data === 'object') {
          // If channel.data exists, copy it BUT exclude language (we'll get it from Firestore)
          const { language, ...streamDataWithoutLanguage } = channel.data;
          channelDataObj = { ...streamDataWithoutLanguage };
        } else {
          // If channel.data doesn't exist, check other possible locations
          // Some Stream channels might store name/image at the top level
          if (channel.name) channelDataObj.name = channel.name;
          if (channel.image) channelDataObj.image = channel.image;
          if (channel.description) channelDataObj.description = channel.description;
        }
        
        // Ensure we have at least an empty object
        if (!channelDataObj || typeof channelDataObj !== 'object') {
          channelDataObj = {};
        }
        
        // Initialize language as undefined - will be set from Firestore
        // This ensures we don't use Stream's default/incorrect language
        channelDataObj.language = undefined;

        const channelData = {
          id: channel.id,
          type: channel.type,
          cid: channel.cid,
          created_at: channel.created_at,
          updated_at: channel.updated_at,
          last_message_at: channel.last_message_at,
          data: channelDataObj, // Always ensure this is an object
          state: {
            members: channel.state?.members || {},
            messages: channel.state?.messages || [],
            read: channel.state?.read || {},
          },
          // Get member IDs from state
          members: Object.keys(channel.state?.members || {}),
        };

        // For groups and classes, fetch Firestore data to enrich metadata
        // This is especially important for channels that were just created
        if (
          channel.type === "standard_group" ||
          channel.type === "premium_group"
        ) {
          try {
            const groupDoc = await getDoc(doc(db, "groups", channel.id));
            if (groupDoc.exists()) {
              const groupData = groupDoc.data();
              
              // Verify user is actually a member in Firestore
              // Check if user is the admin OR in memberIds
              // This handles the case where tutors create groups (they're admin but not in memberIds initially)
              const memberIds = groupData.memberIds || [];
              const groupAdminId = groupData.groupAdminId;
              const isAdmin = groupAdminId === userId;
              const isMember = memberIds.includes(userId);
              
              // User must be either the admin or a member
              if (!isAdmin && !isMember) {
                // User is not a member or admin in Firestore, skip this channel silently
                // This is expected for channels where user was removed or channel was deleted
                return null; // Filter this out later
              }
              
              // Firestore is the source of truth for language
              const firestoreLanguage = groupData.groupLearningLanguage;
              if (firestoreLanguage) {
                // Directly set the language from Firestore (no fallback)
                channelData.data.language = String(firestoreLanguage).trim();
              } else {
                // Set default only if missing
                channelData.data.language = "English";
              }
              
              if (groupData.groupName) {
                channelData.data.name = groupData.groupName;
              }
              if (groupData.groupDescription) {
                channelData.data.description = groupData.groupDescription;
              }
              if (groupData.imageUrl) {
                channelData.data.image = groupData.imageUrl;
              }
              
              // Add groupId to custom data for consistency
              if (!channelData.data.custom) {
                channelData.data.custom = {};
              }
              channelData.data.groupId = channel.id;
              channelData.data.channelType = channel.type;
              channelData.data.custom.groupId = channel.id;
              channelData.data.custom.channelType = channel.type;
            } else {
              // Group doesn't exist in Firestore, skip this channel silently
              // This is expected for orphaned channels in Stream (e.g., deleted groups)
              return null;
            }
          } catch (err) {
            console.error(`[getUserChannels] Could not fetch group data for ${channel.id}:`, err);
            return null; // Skip on error
          }
        } else if (channel.type === "premium_individual_class") {
          try {
            const classDoc = await getDoc(doc(db, "classes", channel.id));
            if (classDoc.exists()) {
              const classData = classDoc.data();
              
              // Verify user is actually a member in Firestore
              const classMemberIds = classData.classMemberIds || [];
              const classAdminId = classData.adminId;
              const isClassAdmin = classAdminId === userId;
              const isClassMember = classMemberIds.includes(userId);
              
              // User must be either the admin or a member
              if (!isClassAdmin && !isClassMember) {
                // User is not a member or admin in Firestore, skip this channel silently
                // This is expected for channels where user was removed or channel was deleted
                return null; // Filter this out later
              }
              
              // Always enrich with Firestore data (it's the source of truth)
              // CRITICAL: Set language FIRST before other fields to ensure it's not overwritten
              // Firestore is the source of truth for language
              const firestoreLanguage = classData.language;
              if (firestoreLanguage) {
                // Directly set the language from Firestore (no fallback)
                channelData.data.language = String(firestoreLanguage).trim();
              } else {
                // Set default only if missing
                channelData.data.language = "English";
              }
              
              if (classData.className) {
                channelData.data.name = classData.className;
              }
              if (classData.classDescription) {
                channelData.data.description = classData.classDescription;
              }
              if (classData.imageUrl) {
                channelData.data.image = classData.imageUrl;
              }
              
              // Add classId to custom data for consistency
              if (!channelData.data.custom) {
                channelData.data.custom = {};
              }
              channelData.data.classId = channel.id;
              channelData.data.channelType = channel.type;
              channelData.data.custom.classId = channel.id;
              channelData.data.custom.channelType = channel.type;
            } else {
              // Class doesn't exist in Firestore, skip this channel silently
              // This is expected for orphaned channels in Stream (e.g., deleted classes)
              return null;
            }
          } catch (err) {
            console.error(`[getUserChannels] Could not fetch class data for ${channel.id}:`, err);
            return null; // Skip on error
          }
        } else if (channel.type === "exam_prep") {
          // For exam_prep channels, ensure we preserve the created_by (tutor) info
          // The created_by is typically set when the channel is created
          if (channel.created_by) {
            channelData.created_by = channel.created_by;
          }
          // Ensure exam_prep channels have proper data structure
          if (!channelData.data.name) {
            channelData.data.name = "Exam Prep";
          }
        }

        // Final safety check - ensure data object exists and is not null/undefined
        if (!channelData.data || typeof channelData.data !== 'object') {
          channelData.data = {};
        }

        return channelData;
      })
    )).filter(ch => ch !== null); // Filter out null channels (not members or don't exist)

    return enrichedChannels;
  } catch (error) {
    console.error("[getUserChannels] Error querying Stream directly:", error);
    // Fallback: return empty array on error
    return [];
  }
}

export function getTutorClasses(tutorId, token) {
  return apiRequest(
    `https://gettutorclasses-${API_BASE_URL}?tutorId=${encodeURIComponent(
      tutorId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function getStudentClasses(studentId, token) {
  return apiRequest(
    `https://getstudentclasses-${API_BASE_URL}?studentId=${encodeURIComponent(
      studentId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function getExamPrepStepStatus(studentId, tutorId, token) {
  return apiRequest(
    `https://getexamprepstepstatus-${API_BASE_URL}?studentId=${encodeURIComponent(
      studentId,
    )}&tutorId=${encodeURIComponent(tutorId)}`,
    {
      method: "GET",
    },
    token,
  );
}

export function getExamPrepPlanTimeline(studentId, token) {
  return apiRequest(
    `https://getexamprepplantimeline-${API_BASE_URL}?studentId=${encodeURIComponent(
      studentId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function getStudentExamPrepTutorialStatus(studentId, token) {
  const functionName = "getstudentexampreptutorialstatus";
  const url = `https://${functionName}-${API_BASE_URL}?studentId=${encodeURIComponent(studentId)}`;
  const options = {
    method: "GET",
  };
  return apiRequest(url, options, token);
}

export function getTutorProfile(tutorId, token) {
  return apiRequest(
    `https://gettutorprofile-${API_BASE_URL}?tutorId=${encodeURIComponent(
      tutorId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

export function updateTutorProfile(data, token) {
  return apiRequest(
    `https://updatetutorprofile-${API_BASE_URL}`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
    token,
  );
}

export function getExamPrepCurrentStep(studentId, token) {
  return apiRequest(
    `https://getexamprepcurrentstep-${API_BASE_URL}?studentId=${encodeURIComponent(
      studentId,
    )}`,
    {
      method: "GET",
    },
    token,
  );
}

//dummy just to check if the api is working
export function getExamPrepStepStatusMock(studentId, tutorId, token) {
  return apiRequest(
    `https://getexamprepstepstatusdev-${API_BASE_URL}?studentId=${encodeURIComponent(
      studentId,
    )}&tutorId=${encodeURIComponent(tutorId)}`,
    {
      method: "GET",
    },
    token,
  );
}

// Add more as needed for new endpoints
